import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck, PlayCircle, ClipboardCheck, Award,
  Users2, CalendarRange, FileCheck2, Bot, Bell, Check, Menu, X,
  MapPin, Phone, Mail, Loader2, CheckCircle2, Building2,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxjYXJlZ2l2ZXIlMjBzbWlsaW5nfGVufDB8fHx8MTc4NzQ2NTY2MHww&ixlib=rb-4.1.0&q=85";
const CTA_IMG = "https://images.pexels.com/photos/7345461/pexels-photo-7345461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay, type: "spring", stiffness: 60, damping: 18 }}
    className={className}
  >
    {children}
  </motion.div>
);

const Overline = ({ children }) => (
  <span className="text-xs font-bold tracking-[0.2em] uppercase text-sky-700 font-sans mb-4 block">{children}</span>
);

const FEATURES = [
  { icon: PlayCircle, title: "動画eラーニング", body: "法定研修・社内研修を動画＋チャプターで配信。続きから再生、視聴履歴を自動記録。", span: "md:col-span-7", img: "https://images.unsplash.com/photo-1543269664-76bc3997d9ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHx0YWJsZXQlMjBsZWFybmluZ3xlbnwwfHx8fDE3ODc0NjU2NjV8MA&ixlib=rb-4.1.0&q=85" },
  { icon: ClipboardCheck, title: "理解度確認テスト", body: "単一/複数選択・○×・記述を自動採点。合格基準で修了判定。", span: "md:col-span-5" },
  { icon: Award, title: "修了証を自動発行", body: "修了と同時に日本語PDFの修了証を発行。誰が・いつ・何を修了したかが証拠として残ります。", span: "md:col-span-5" },
  { icon: Users2, title: "委員会・議事録", body: "委員会運営と議事録、職員周知の「確認しました」までを一元管理。", span: "md:col-span-7", img: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBtZWV0aW5nJTIwd2FybXxlbnwwfHx8fDE3ODc0NjU2NjV8MA&ixlib=rb-4.1.0&q=85" },
  { icon: CalendarRange, title: "年間計画・実施記録", body: "月次グリッドで年間研修計画を作成し、実施記録を法定帳票として出力。", span: "md:col-span-4" },
  { icon: FileCheck2, title: "実地指導・監査対応", body: "緑/黄/赤で準備状況を可視化。実地指導レポートをワンクリックPDF出力。", span: "md:col-span-4" },
  { icon: Bot, title: "AI業務支援", body: "研修案・議事録・所感分析・年度総括をAIが下書き（管理者確認前表示）。", span: "md:col-span-4" },
];

const FLOW = [
  { n: "01", t: "アカウント発行", d: "法人・事業所・職員を登録。CSV一括登録にも対応。" },
  { n: "02", t: "研修を配信", d: "マスター教材から研修を追加し、職員へ割当・配信。" },
  { n: "03", t: "受講・修了判定", d: "職員が動画受講→理解度テスト→自動で修了判定。" },
  { n: "04", t: "証拠を残す", d: "修了証・実施記録・帳票を出力し、実地指導に備える。" },
];

const FAQ = [
  { q: "対応しているサービス種別は？", a: "就労継続支援B型をはじめ、主要8種のサービス種別テンプレートに対応。法定研修・委員会・訓練セットをワンクリックで追加できます。" },
  { q: "無料で試せますか？", a: "はい。トライアル期間をご用意しています。お問い合わせフォームよりお気軽にご連絡ください。" },
  { q: "実地指導・監査に使えますか？", a: "研修の実施記録・修了証・年間計画・議事録・訓練記録をPDF帳票として出力でき、実地指導モードで準備状況を可視化できます。" },
  { q: "職員への周知や通知はできますか？", a: "サイト内メールを標準提供。LINE・WhatsApp連携（無料枠）や受講期限リマインドの自動配信にも対応します。" },
  { q: "セキュリティは？", a: "法人・事業所単位のテナント分離、役割ベースのアクセス制御、操作履歴（監査ログ）、ログイン失敗ロックを標準搭載しています。" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [lp, setLp] = useState(null);
  const [plans, setPlans] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get("/public/lp-settings").then(({ data }) => setLp(data)).catch(() => setLp({}));
    api.get("/public/plans").then(({ data }) => setPlans(data || [])).catch(() => setPlans([]));
  }, []);

  const s = lp || {};
  const nav = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      setForm({ name: "", organization: "", email: "", phone: "", message: "" });
      toast.success("お問い合わせを送信しました");
    } catch (err) { toast.error(apiErr(err)); } finally { setSending(false); }
  };

  const NAV_LINKS = [["features", "主要機能"], ["flow", "導入フロー"], ["pricing", "料金"], ["faq", "FAQ"], ["contact", "お問い合わせ"]];

  return (
    <div className="min-h-screen bg-[#FAFAF9] text-slate-900 font-sans antialiased overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="lp-logo" className="flex items-center gap-2.5">
            <img src="/wcc-icon.png" alt="WCC" className="w-9 h-9 rounded-xl object-contain shadow-sm" />
            <span className="font-serif text-lg font-bold tracking-tight">{s.service_name || "Welfy Care Campus"}</span>
          </button>
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map(([id, label]) => (
              <button key={id} onClick={() => nav(id)} data-testid={`lp-nav-${id}`} className="text-sm text-slate-600 hover:text-sky-700 transition-colors">{label}</button>
            ))}
            <button onClick={() => navigate("/guide")} data-testid="lp-nav-guide" className="text-sm text-slate-600 hover:text-sky-700 transition-colors">使い方</button>
          </nav>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/login")} data-testid="lp-login-btn"
              className="rounded-full bg-sky-700 hover:bg-sky-600 text-white px-5 transition-colors">
              ログイン <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <button className="md:hidden p-2" onClick={() => setMenuOpen((v) => !v)} data-testid="lp-menu-toggle">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white px-5 py-3 space-y-1">
            {NAV_LINKS.map(([id, label]) => (
              <button key={id} onClick={() => nav(id)} className="block w-full text-left py-2 text-sm text-slate-700">{label}</button>
            ))}
            <button onClick={() => { setMenuOpen(false); navigate("/guide"); }} className="block w-full text-left py-2 text-sm text-slate-700">使い方</button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative pt-28 lg:pt-36 pb-20 lg:pb-28">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-50 via-white to-[#FAFAF9] -z-10" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="max-w-xl">
            <Overline>障がい福祉サービス事業者向け SaaS</Overline>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-[1.15] text-slate-900">
              {s.hero_title || "障がい福祉の研修・法定管理を、ひとつに。"}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              {s.hero_description}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button onClick={() => nav("contact")} data-testid="lp-hero-cta"
                className="rounded-full bg-sky-700 hover:bg-sky-600 text-white px-7 h-12 text-base transition-colors">
                無料で相談する <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button onClick={() => nav("features")} data-testid="lp-hero-features" variant="outline"
                className="rounded-full border-slate-300 px-7 h-12 text-base hover:bg-white transition-colors">
                機能を見る
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-5 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-500" /> テナント分離・監査ログ</span>
              <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-emerald-500" /> 修了証を自動発行</span>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.12)]">
              <img src={HERO_IMG} alt="福祉現場の様子" className="w-full h-[380px] lg:h-[480px] object-cover" />
            </div>
            {/* floating glass stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="absolute -left-4 lg:-left-10 top-10 backdrop-blur-xl bg-white/85 border border-white/60 shadow-lg rounded-2xl px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-500" /></div>
                <div>
                  <div className="text-2xl font-bold text-slate-900 leading-none">98<span className="text-base">%</span></div>
                  <div className="text-xs text-slate-500 mt-1">研修修了率</div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
              className="absolute -right-3 lg:-right-8 bottom-8 backdrop-blur-xl bg-white/85 border border-white/60 shadow-lg rounded-2xl px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center"><Bell className="w-5 h-5 text-sky-700" /></div>
                <div>
                  <div className="text-sm font-bold text-slate-900 leading-tight">受講リマインド</div>
                  <div className="text-xs text-slate-500 mt-0.5">期限前に自動通知</div>
                </div>
              </div>
            </motion.div>
          </Reveal>
        </div>
      </section>

      {/* Challenges (dark) */}
      <section className="bg-slate-900 text-white py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-amber-300 mb-4 block">現場の課題</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight leading-tight">
              研修も、法定記録も、<br className="hidden sm:block" />紙とExcelでバラバラになっていませんか。
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ["実施の証拠が残らない", "誰がいつ何を受講・修了したのか、実地指導で示せる証拠が散在している。"],
              ["管理の負担が大きい", "研修計画・委員会・訓練・帳票の作成と進捗管理に、毎年膨大な手間がかかる。"],
              ["監査対応が不安", "実地指導の直前に、書類の抜け漏れを慌てて確認している。"],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.1}>
                <div className="border-t border-slate-700 pt-6">
                  <div className="text-4xl font-serif text-amber-300/90">0{i + 1}</div>
                  <h3 className="mt-4 text-xl font-bold">{t}</h3>
                  <p className="mt-3 text-slate-400 leading-relaxed text-sm">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section id="features" className="py-24 lg:py-32 bg-gradient-to-b from-sky-50/40 to-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Overline>主要機能</Overline>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-slate-900">研修から法定管理まで、これひとつ。</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">現場の「実施したこと」と「その証拠」を、ひとつのクラウドで確実に残します。</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-7">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 0.08} className={`col-span-1 ${f.span}`}>
                <div className="group h-full bg-white rounded-3xl border border-slate-200/80 p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
                  <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center mb-5">
                    <f.icon className="w-6 h-6 text-sky-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 font-sans">{f.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-sm">{f.body}</p>
                  {f.img && (
                    <div className="mt-6 rounded-2xl overflow-hidden">
                      <img src={f.img} alt={f.title} className="w-full h-40 object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Flow */}
      <section id="flow" className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Overline>導入フロー</Overline>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-slate-900">最短4ステップで運用開始。</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-8">
            {FLOW.map((step, i) => (
              <Reveal key={step.n} delay={i * 0.1}>
                <div className="relative">
                  <div className="text-5xl font-serif text-sky-700/20 font-bold">{step.n}</div>
                  {i < FLOW.length - 1 && <div className="hidden md:block absolute top-6 left-20 right-0 border-t border-dashed border-slate-300" />}
                  <h3 className="mt-3 text-lg font-bold text-slate-800">{step.t}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{step.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 lg:py-32 bg-gradient-to-b from-[#FAFAF9] to-sky-50/40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Overline>料金プラン</Overline>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-slate-900">わかりやすい月額・年額プラン。</h2>
            <p className="mt-5 text-slate-600">表示価格は税抜です。詳細はお問い合わせください。</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {plans.length === 0 && (
              <div className="col-span-3 text-center text-slate-400 py-10">プラン情報を読み込み中…</div>
            )}
            {plans.map((p, i) => {
              const highlight = plans.length >= 2 && i === 1;
              return (
                <Reveal key={p.lookup_key} delay={i * 0.08}>
                  <div data-testid={`lp-plan-${p.lookup_key}`}
                    className={`h-full flex flex-col rounded-3xl bg-white p-8 lg:p-10 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${highlight ? "border-2 border-sky-700 shadow-[0_20px_40px_rgb(3,105,161,0.12)]" : "border border-slate-200"}`}>
                    {highlight && <span className="self-start mb-3 text-xs font-bold tracking-wide text-sky-700 bg-sky-50 rounded-full px-3 py-1">おすすめ</span>}
                    <h3 className="text-lg font-bold text-slate-800">{p.name}</h3>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-4xl font-serif font-bold text-slate-900">¥{(p.price || 0).toLocaleString()}</span>
                      <span className="text-sm text-slate-500">/ {p.interval}</span>
                    </div>
                    <ul className="mt-6 space-y-3 flex-1">
                      {(p.features || []).map((ft) => (
                        <li key={ft} className="flex items-start gap-2.5 text-sm text-slate-600">
                          <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {ft}
                        </li>
                      ))}
                    </ul>
                    <Button onClick={() => navigate(`/register?plan=${p.lookup_key}`)} data-testid={`lp-plan-cta-${p.lookup_key}`}
                      className={`mt-8 rounded-full h-11 transition-colors ${highlight ? "bg-sky-700 hover:bg-sky-600 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                      このプランで申し込む
                    </Button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Overline>よくあるご質問</Overline>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold tracking-tight text-slate-900">FAQ</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} data-testid={`lp-faq-${i}`} className="border-b border-slate-200">
                  <AccordionTrigger className="text-left text-base font-medium text-slate-800 hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed text-sm pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 lg:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-amber-300 mb-4 block">お問い合わせ</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight leading-tight">導入のご相談・<br />資料請求はこちら。</h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              サービス内容・料金・トライアルについて、お気軽にお問い合わせください。担当より折り返しご連絡いたします。
            </p>
            <div className="mt-10 space-y-4 text-sm">
              {s.company_name && <div className="flex items-center gap-3 text-slate-200"><Building2 className="w-4 h-4 text-amber-300 shrink-0" /> {s.company_name}</div>}
              {s.address && <div className="flex items-center gap-3 text-slate-300"><MapPin className="w-4 h-4 text-amber-300 shrink-0" /> {s.address}</div>}
              {s.phone && <div className="flex items-center gap-3 text-slate-300"><Phone className="w-4 h-4 text-amber-300 shrink-0" /> {s.phone}</div>}
              {s.email && <div className="flex items-center gap-3 text-slate-300"><Mail className="w-4 h-4 text-amber-300 shrink-0" /> {s.email}</div>}
            </div>
            <div className="mt-8 rounded-2xl overflow-hidden max-w-sm">
              <img src={CTA_IMG} alt="サポート" className="w-full h-44 object-cover" />
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="bg-white text-slate-900 rounded-3xl p-8 lg:p-10 shadow-2xl">
              {sent ? (
                <div className="text-center py-10" data-testid="lp-contact-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-emerald-500" /></div>
                  <h3 className="mt-5 text-xl font-bold">送信が完了しました</h3>
                  <p className="mt-3 text-slate-600 text-sm">お問い合わせありがとうございます。<br />担当より折り返しご連絡いたします。</p>
                  <Button onClick={() => setSent(false)} variant="outline" className="mt-6 rounded-full" data-testid="lp-contact-again">続けて送信する</Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="c-name">お名前 <span className="text-rose-500">*</span></Label>
                      <Input id="c-name" data-testid="lp-contact-name" required value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="山田 太郎" />
                    </div>
                    <div>
                      <Label htmlFor="c-org">法人・事業所名</Label>
                      <Input id="c-org" data-testid="lp-contact-org" value={form.organization}
                        onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1.5" placeholder="社会福祉法人◯◯" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="c-email">メールアドレス <span className="text-rose-500">*</span></Label>
                      <Input id="c-email" type="email" data-testid="lp-contact-email" required value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="you@example.com" />
                    </div>
                    <div>
                      <Label htmlFor="c-phone">電話番号</Label>
                      <Input id="c-phone" data-testid="lp-contact-phone" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" placeholder="03-0000-0000" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-msg">お問い合わせ内容 <span className="text-rose-500">*</span></Label>
                    <Textarea id="c-msg" data-testid="lp-contact-message" required value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" rows={4} placeholder="ご相談内容をご記入ください" />
                  </div>
                  <Button type="submit" disabled={sending} data-testid="lp-contact-submit"
                    className="w-full rounded-full h-12 bg-sky-700 hover:bg-sky-600 text-white text-base transition-colors">
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <>送信する <ArrowRight className="w-4 h-4 ml-1.5" /></>}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/wcc-icon.png" alt="WCC" className="w-8 h-8 rounded-lg object-contain bg-white/10 p-0.5" />
            <span className="font-serif font-bold text-white">{s.service_name || "Welfy Care Campus"}</span>
          </div>
          <div className="text-xs">© 2026 {s.company_name || "Welfy Care Campus 運営事務局"}</div>
          <button onClick={() => navigate("/login")} data-testid="lp-footer-login" className="text-sm text-slate-300 hover:text-white transition-colors">ログイン →</button>
        </div>
      </footer>
    </div>
  );
}
